import maplibregl, { LngLatBoundsLike } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import React, { useRef, useEffect, FC } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { SiteGeo } from '../mock-data';
import { CloudOff, WifiOff, ChevronRight } from 'lucide-react';

export const MapTile: FC<{
  data: any;
  onClickSiteButton: any;
  onClickSite: any;
  onResetView?: () => void;
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}> = ({
  data,
  minLon,
  minLat,
  maxLon,
  maxLat,
  onClickSiteButton,
  onClickSite,
  onResetView,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<Array<maplibregl.Marker>>([]);

  const API_KEY = 'ckN8DwiThHM7dF7gYVVM';

  const addMarkers = (data: any) => {
    markers.current.forEach((marker) => marker.remove());
    markers.current = [];

    // Sort data so offline markers are rendered first (appear below online markers)
    const sortedData = [...data].sort((a, b) => {
      // disconnectIconDisplay === 'block' means offline
      const aIsOffline = a.disconnectIconDisplay === 'block';
      const bIsOffline = b.disconnectIconDisplay === 'block';
      
      // Offline markers first (0), online markers last (1)
      if (aIsOffline && !bIsOffline) return -1;
      if (!aIsOffline && bIsOffline) return 1;
      return 0;
    });

    const newMarkers = sortedData.map((item: any) => {
      const latitude = item.lat;
      const longitude = item.lon;

      const el = renderToStaticMarkup(
        <div
          style={{
            position: 'relative',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // Higher z-index for online markers
            zIndex: item.disconnectIconDisplay === 'block' ? 1 : 10,
          }}
          title={`${item.name} (${item.province})\nEUI: ${item.value} kWh/m²·yr\nStatus: ${item.disconnectIconDisplay === 'block' ? 'Offline' : item.hasAlarm ? 'Alarm' : 'Online'}\nLast updated: ${item.updatedAt}`}
        >
          {/* Compact floating bubble */}
          <div
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              padding: '1px 6px',
              borderRadius: '8px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              border: `1px solid ${item.hasAlarm ? 'rgba(232, 45, 45, 0.6)' : 'rgba(0,0,0,0.08)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              position: 'relative',
              zIndex: item.disconnectIconDisplay === 'block' ? 1 : 10,
            }}
          >
            {/* Branch code */}
            <div style={{ 
              fontSize: '9px', 
              fontWeight: '500', 
              color: item.disconnectIconDisplay === 'block' ? '#9CA3AF' : '#000000',
              letterSpacing: '0.02em',
            }}>
              {item.code}
            </div>
            
            {/* Efficiency value */}
            <div style={{ 
              fontSize: '10px', 
              fontWeight: '700', 
              color: item.disconnectIconDisplay === 'block' ? '#9CA3AF' : item.valueColor,
              letterSpacing: '-0.02em',
            }}>
              {item.value}
            </div>
          </div>
          
          {/* Minimal connecting line */}
          <div style={{
            width: '1px',
            height: '12px',
            backgroundColor: item.disconnectIconDisplay === 'block' ? '#9CA3AF' : item.statusColor,
            opacity: 0.4,
          }} />
          
          {/* Minimal pin dot */}
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: item.disconnectIconDisplay === 'block' ? '#9CA3AF' : item.statusColor,
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 1,
          }} />
        </div>
      );

      const div = document.createElement('div');
      div.innerHTML = el.trim();
      div.addEventListener('click', () => onClickSite(item.id));

      const marker = new maplibregl.Marker({ 
        element: div,
        offset: [0, -10] // Offset for line and dot
      })
        .setLngLat([longitude, latitude])
        .addTo(map.current as maplibregl.Map);

      return marker;
    });

    markers.current = newMarkers;
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current as HTMLElement,
      style: `https://api.maptiler.com/maps/basic-v2/style.json?key=${API_KEY}`,
      center: [0, 0],
      zoom: 9,
    });

    map.current.addControl(new maplibregl.FullscreenControl(), 'top-right');
    map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

    // Add double-click event to reset to all properties
    map.current.on('dblclick', (e) => {
      e.preventDefault();
      if (onResetView) {
        onResetView();
      }
    });

    const handleResize = () => {
      if (map.current && data.length > 0) {
        map.current.fitBounds(
          [
            [minLon, minLat],
            [maxLon, maxLat],
          ] as LngLatBoundsLike,
          {
            padding: { top: 60, bottom: 60, left: 80, right: 80 },
            duration: 0,
            pitch: maxLat - minLat <= 0.005 && maxLon - minLon <= 0.005 ? 30 : 0,
          }
        );
      }
    };

    map.current.on('resize', handleResize);
    addMarkers(data);

    return () => {
      map.current?.off('resize', handleResize);
      map.current?.remove();
    };
  }, []);

  useEffect(() => {
    if (map.current) {
      addMarkers(data);
      return;
    }
  }, [data, map.current]);

  useEffect(() => {
    if (map.current && data.length > 0) {
      map.current.fitBounds(
        [
          [minLon, minLat],
          [maxLon, maxLat],
        ] as LngLatBoundsLike,
        {
          padding: { top: 60, bottom: 60, left: 80, right: 80 },
          duration: 1000,
          pitch:
            maxLat - minLat <= 0.005 && maxLon - minLon <= 0.005 ? 30 : 0,
        }
      );
    }
  }, [minLon, minLat, maxLon, maxLat]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <div
        ref={mapContainer}
        style={{ position: 'absolute', width: '100%', height: '100%' }}
      />
    </div>
  );
};